import React, { useState } from "react";
import { SlowestEvent } from "../../../types/outbox.types";
import { LaneBadge } from "./LaneBadge";
import { format, parseISO } from "date-fns";

interface SlowestEventsListProps {
  events24h: SlowestEvent[];
  events1h: SlowestEvent[];
}

const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60_000;

const formatDateTime = (isoString: string): string => {
  try {
    const date = parseISO(isoString);
    return format(date, "yyyy-MM-dd HH:mm:ss");
  } catch {
    return isoString;
  }
};

function formatDuration(ms: number): string {
  if (ms < MS_PER_SECOND) return `${ms.toFixed(0)}ms`;
  if (ms < MS_PER_MINUTE) return `${(ms / MS_PER_SECOND).toFixed(1)}s`;
  return `${(ms / MS_PER_MINUTE).toFixed(1)}min`;
}

const JsonSection: React.FC<{
  label: string;
  data: Record<string, unknown> | null;
}> = ({ label, data }) => {
  if (data === null) {
    return (
      <div>
        <h4 className="text-sm font-semibold mb-2 text-neutral-700">{label}</h4>
        <div className="text-xs text-neutral-400 italic p-3">Brak danych</div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-semibold mb-2 text-neutral-700">{label}</h4>
      <pre className="text-xs font-mono bg-neutral-50 border border-neutral-200 p-3 rounded overflow-auto max-h-96">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

type TabKey = "1h" | "24h";

const SlowestEventsTable: React.FC<{ events: SlowestEvent[] }> = ({ events }) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (eventId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const handleKeyDown = (eventId: string, e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleExpanded(eventId);
    }
  };

  if (!events.length) {
    return (
      <div className="text-neutral-500 text-center py-8">
        Brak przetworzonych eventów w tym okresie
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.map((event) => {
        const isExpanded = expandedIds.has(event.id);
        const detailsId = `outbox-slowest-details-${event.id}`;

        return (
          <div
            key={event.id}
            className="border border-neutral-200 rounded-lg hover:border-neutral-400 transition-colors"
          >
            <div
              data-testid={`outbox-slowest-row-${event.id}`}
              role="button"
              aria-expanded={isExpanded}
              aria-controls={detailsId}
              tabIndex={0}
              className="p-4 cursor-pointer"
              onClick={() => toggleExpanded(event.id)}
              onKeyDown={(e) => handleKeyDown(event.id, e)}
            >
              <div className="flex items-start gap-2">
                <svg
                  className={`w-4 h-4 mt-1 text-neutral-400 transition-transform flex-shrink-0 ${isExpanded ? "rotate-90" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <code className="text-sm font-mono text-neutral-900 bg-neutral-100 px-2 py-1 rounded">
                      {event.eventName}
                    </code>
                    <LaneBadge lane={event.lane} />
                  </div>

                  <div className="text-sm text-neutral-600 mb-1">
                    <span className="font-medium">Created:</span>{" "}
                    {formatDateTime(event.createdAt)}
                  </div>

                  <div className="flex gap-4 text-sm">
                    <span className="text-neutral-500">
                      Processing: <span className="font-mono font-semibold text-amber-700">{formatDuration(event.processingMs)}</span>
                    </span>
                    <span className="text-neutral-500">
                      Queue: <span className="font-mono text-neutral-700">{formatDuration(event.queueWaitMs)}</span>
                    </span>
                    <span className="text-neutral-500">
                      End-to-End: <span className="font-mono text-neutral-700">{formatDuration(event.endToEndMs)}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {isExpanded && (
              <div
                id={detailsId}
                data-testid={detailsId}
                className="border-t border-neutral-200 p-4 space-y-4"
              >
                <JsonSection label="Payload" data={event.payload} />
                <JsonSection label="Metadata" data={event.metadata} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const SlowestEventsList: React.FC<SlowestEventsListProps> = ({
  events24h,
  events1h,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>("1h");

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: "1h", label: "Ostatnia godzina", count: events1h.length },
    { key: "24h", label: "Ostatnie 24h", count: events24h.length },
  ];

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-6"
      data-testid="outbox-slowest-list"
    >
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">
        Najwolniejsze eventy
      </h3>

      <div className="flex gap-2 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeTab === tab.key
                ? "bg-neutral-900 text-white"
                : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <SlowestEventsTable events={activeTab === "1h" ? events1h : events24h} />
    </div>
  );
};

export default SlowestEventsList;
